import type {
	BaseItemSchema,
	Item,
	MixedItemTag,
	OriginalItemTypes,
	ReportDetails,
} from "@memorang/types";
import {
	getItemScore,
	isAttempted,
	isItemAnswered,
	isItemAnsweredCorrectly,
	isItemMarked,
	isItemReviewable,
} from "./item";
import { isQuestionTypeTag, isSkillTag } from "./tag";

export type QuestionItem = Item & { variant: OriginalItemTypes };

export type SkillFilter = "all" | string;

export type QuestionTypeRowData = {
	questionType: string;
	numItems: number;
	distribution: string;
};

export const getReportSkills = (
	tags: MixedItemTag[],
	topics: string[],
): MixedItemTag[] => {
	return tags.filter(
		(tag) =>
			isSkillTag(tag) &&
			(topics.length === 0 || topics.includes(tag.title.toLowerCase())),
	);
};

export const isQuestionItem = (item: Item): item is QuestionItem =>
	item.type === "question" && item.variant != null;

export const collectQuestionItemsForSkillFilter = (
	tags: MixedItemTag[],
	skillFilter: SkillFilter,
): Item[] => {
	const skillTags = tags.filter((tag) => isSkillTag(tag));

	if (skillFilter !== "all") {
		const skillTag = skillTags.find((tag) => tag.title === skillFilter);
		return skillTag?.items ?? [];
	}

	if (skillTags.length > 0) {
		return skillTags.flatMap((tag) => tag.items ?? []);
	}

	return tags
		.filter((tag) => isQuestionTypeTag(tag))
		.flatMap((tag) => tag.items ?? []);
};

export const getQuestionItemsForSkillFilter = (
	tags: MixedItemTag[],
	skillFilter: SkillFilter,
): QuestionItem[] => {
	return collectQuestionItemsForSkillFilter(tags, skillFilter).filter(
		isQuestionItem,
	);
};

export const isReportSummativeTest = (
	report: ReportDetails,
	options?: { studyPackCollectionId: number },
) => {
	return (
		report.reportSessionType === "SUMMATIVE" &&
		options?.studyPackCollectionId !== 38
	);
};

export type ItemTypeDetailStats = {
	numAnswered: number;
	numTotal: number;
	/** Whole points earned across the item type; null when the type has no point scale. */
	pointsEarned: number | null;
	/** Points available across the item type; null when no maximum is known. */
	pointsTotal: number | null;
	avgTimeSeconds: number | null;
};

export const emptyItemTypeDetailStats: ItemTypeDetailStats = {
	numAnswered: 0,
	numTotal: 0,
	pointsEarned: null,
	pointsTotal: null,
	avgTimeSeconds: null,
};

const roundOneDecimal = (value: number) => Math.round(value * 10) / 10;

const sum = (values: number[]): number | null => {
	if (values.length === 0) {
		return null;
	}

	return values.reduce((total, value) => total + value, 0);
};

const mean = (values: number[]): number | null => {
	if (values.length === 0) {
		return null;
	}

	return values.reduce((total, value) => total + value, 0) / values.length;
};

const isVariantItemAnswered = ({
	item,
	sessionItem,
	isSummativeTest,
}: {
	item: QuestionItem;
	sessionItem?: BaseItemSchema;
	isSummativeTest: boolean;
}) => {
	// The session item carries the user's actual response; the report
	// distribution buckets skipped items by grade, so it cannot tell a skip
	// apart from a wrong answer. Fall back to it only when items are absent.
	if (sessionItem != null) {
		return isItemAnswered({
			item: sessionItem,
			isSummativeTest,
			inReviewMode: true,
		});
	}

	return item.distribution != null ? isAttempted(item) : false;
};

const getSessionItemById = (sessionItems: BaseItemSchema[]) => {
	return new Map(
		sessionItems
			.filter((sessionItem) => sessionItem.id != null)
			.map((sessionItem) => [sessionItem.id, sessionItem]),
	);
};

/** Without a point maximum the session score is a 0-100 percentage, not points. */
const getSessionPointScore = (sessionItem?: BaseItemSchema) => {
	if (sessionItem == null) {
		return null;
	}

	const { score, maxScore } = getItemScore(sessionItem);
	if (maxScore == null || maxScore <= 0) {
		return null;
	}

	return { score, maxScore };
};

type ItemPoints = { earned: number | null; total: number | null };

/** Earning more than the maximum means the two came from different scales. */
const dropIncoherentTotal = ({ earned, total }: ItemPoints): ItemPoints =>
	earned != null && total != null && earned > total
		? { earned, total: null }
		: { earned, total };

/**
 * The session item is what the answer rows render, so it wins; the report's
 * `numPointsEarned`/`numTotalPoints` pair is only a fallback for unhydrated sessions.
 * Earned and total must come from the same scale, so each source is read as a pair.
 */
const resolveItemPoints = (
	item: QuestionItem,
	sessionItem?: BaseItemSchema,
): ItemPoints => {
	const sessionPoints = getSessionPointScore(sessionItem);
	if (sessionPoints != null) {
		return dropIncoherentTotal({
			earned: sessionPoints.score,
			total: sessionPoints.maxScore,
		});
	}

	const { numPointsEarned, numTotalPoints } = item.stats ?? {};
	if (numPointsEarned != null) {
		return dropIncoherentTotal({
			earned: numPointsEarned,
			total: numTotalPoints ?? null,
		});
	}

	return { earned: null, total: null };
};

/** A non-positive time means nothing was recorded, not that the item took no time. */
const resolveItemTimeSeconds = (
	item: QuestionItem,
	sessionItem?: BaseItemSchema,
): number | null =>
	[
		item.stats?.timeTaken,
		sessionItem?.answer?.timeTaken,
		sessionItem?.answer?.timeElapsed,
	].find((time): time is number => time != null && time > 0) ?? null;

export const buildItemTypeDetailStats = ({
	questionItems,
	sessionItems,
	isSummativeTest,
}: {
	questionItems: QuestionItem[];
	sessionItems: BaseItemSchema[];
	isSummativeTest: boolean;
}): ItemTypeDetailStats => {
	const sessionItemById = getSessionItemById(sessionItems);

	const numTotal = questionItems.length;
	const numAnswered = questionItems.filter((item) =>
		isVariantItemAnswered({
			item,
			sessionItem: sessionItemById.get(item.id),
			isSummativeTest,
		}),
	).length;

	const earnedPoints: number[] = [];
	const totalPoints: number[] = [];
	const itemTimes: number[] = [];

	for (const item of questionItems) {
		const sessionItem = sessionItemById.get(item.id);

		const { earned, total } = resolveItemPoints(item, sessionItem);
		if (earned != null) {
			earnedPoints.push(earned);
		}
		if (total != null) {
			totalPoints.push(total);
		}

		const timeSeconds = resolveItemTimeSeconds(item, sessionItem);
		if (timeSeconds != null) {
			itemTimes.push(timeSeconds);
		}
	}

	const pointsEarnedSum = sum(earnedPoints);
	// A maximum missing from any one item would understate the whole denominator.
	const pointsTotalSum =
		totalPoints.length === earnedPoints.length ? sum(totalPoints) : null;
	const avgTime = mean(itemTimes);

	return {
		numAnswered,
		numTotal,
		pointsEarned: pointsEarnedSum != null ? Math.round(pointsEarnedSum) : null,
		pointsTotal:
			pointsEarnedSum != null && pointsTotalSum != null
				? roundOneDecimal(pointsTotalSum)
				: null,
		avgTimeSeconds: avgTime != null ? Math.round(avgTime) : null,
	};
};

export type ItemsFilter = "all" | "correct" | "incorrect" | "marked";

export const getReviewItems = (
	sessionItems: BaseItemSchema[] | undefined,
): BaseItemSchema[] => {
	return (sessionItems ?? []).filter((sessionItem) =>
		isItemReviewable(sessionItem),
	);
};

export const hasMarkedReviewItems = (
	sessionItems: BaseItemSchema[] | undefined,
): boolean => {
	return getReviewItems(sessionItems).some((sessionItem) =>
		isItemMarked(sessionItem),
	);
};

export const filterReviewItems = (
	sessionItems: BaseItemSchema[] | undefined,
	filter: ItemsFilter,
): BaseItemSchema[] => {
	const reviewItems = getReviewItems(sessionItems);

	switch (filter) {
		case "all":
			return reviewItems;
		case "correct":
			return reviewItems.filter((sessionItem) =>
				isItemAnsweredCorrectly(sessionItem),
			);
		case "incorrect":
			return reviewItems.filter(
				(sessionItem) =>
					sessionItem.answer != null && !isItemAnsweredCorrectly(sessionItem),
			);
		case "marked":
			return reviewItems.filter((sessionItem) => isItemMarked(sessionItem));
		default: {
			const _exhaustiveCheck: never = filter;
			return _exhaustiveCheck;
		}
	}
};

export const resolveItemsFilter = (
	filter: ItemsFilter,
	hasMarkedItems: boolean,
): ItemsFilter => {
	if (filter === "marked" && !hasMarkedItems) {
		return "all";
	}
	return filter;
};

type CanShowSessionReportPremiumCardArgs = {
	isAccessLoading: boolean;
	hasStudyPackPurchaseAccess: boolean;
	isProductReady: boolean;
	features: string[] | undefined;
	imageUrl: string | undefined;
	productName: string | undefined;
};

export const canShowSessionReportPremiumCard = ({
	isAccessLoading,
	hasStudyPackPurchaseAccess,
	isProductReady,
	features,
	imageUrl,
	productName,
}: CanShowSessionReportPremiumCardArgs): boolean => {
	if (isAccessLoading || hasStudyPackPurchaseAccess || !isProductReady) {
		return false;
	}
	if (!features || features.length === 0) {
		return false;
	}
	if (!imageUrl || !productName) {
		return false;
	}
	return true;
};
